import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DownOutlined, LoadingOutlined, MoreOutlined } from '@ant-design/icons';
import { Drawer, Switch } from 'antd';
import { Dropdown } from '@components/Dropdown';
import {
  CloseIcon,
  CommonQAIcon,
  DeleteChatIcon,
  EditChatNameIcon,
  NewChatIcon,
  SearchChatHistoryIcon,
  SearchNoResultIcon,
  SettingIcon,
  ViettelTendooAIDrawer,
  VolumeIcon,
  VolumeMuteIcon,
} from '@assets/icon';
import {
  chatService,
  setMessageSessionId,
  MESSAGE_SESSION_STORAGE_KEY,
  type AiDisplayPosition,
  type AiDisplaySetting,
  type MessageSession,
} from '@services/chatService';
import { ChatAgentType } from '@constants/chat-agents';
import { PostMessageType } from '@constants/postMessage';
import { REACT_APP_ALLOWED_DOMAINS } from '../../../../config';
import { safePostMessage } from '@utils/index';
import './ChatSideSheet.scss';

interface ChatSideSheetProps {
  open: boolean;
  onClose: () => void;
  getContainer?: HTMLElement | false;
  isAudioEnabled?: boolean;
  onToggleAudio?: () => void;
  isBotSpeaking?: boolean;
  typeChat?: string;
  /** Bump after creating a new session so list reloads and shows previous named sessions */
  sessionsRefreshKey?: number;
  /** Personalization ai_display từ runChatbotOpenRequests */
  aiDisplayFromOpen?: AiDisplaySetting | null;
  aiDisplayOpenVersion?: number;
  onNewChat?: () => void | Promise<void>;
  onSelectConversation?: (conversation: { id: string; title: string }) => void;
}

type ConversationItem = {
  id: string;
  title: string;
};

const ALLOWED_DOMAINS: string[] = REACT_APP_ALLOWED_DOMAINS
  ? REACT_APP_ALLOWED_DOMAINS.split(',').map((d) => d.trim()).filter(Boolean)
  : ['localhost:3001', 'localhost:3000'];

const isAllowedParentOrigin = (origin: string) => {
  try {
    const url = new URL(origin);
    const originDomain = url.port ? `${url.hostname}:${url.port}` : url.hostname;
    return ALLOWED_DOMAINS.some(
      (domain) =>
        originDomain === domain ||
        url.hostname.endsWith(domain) ||
        (domain.startsWith('localhost') && url.hostname === 'localhost'),
    );
  } catch {
    return false;
  }
};

const mapSessionToConversation = (session: MessageSession): ConversationItem => ({
  id: session.id,
  title: session.name?.trim() || 'Cuộc trò chuyện',
});

const ChatSideSheet: React.FC<ChatSideSheetProps> = ({
  open,
  onClose,
  getContainer = false,
  isAudioEnabled = true,
  onToggleAudio,
  isBotSpeaking = false,
  typeChat = ChatAgentType.AI,
  sessionsRefreshKey = 0,
  aiDisplayFromOpen = null,
  aiDisplayOpenVersion = 0,
  onNewChat,
  onSelectConversation,
}) => {
  const [activeConversationMenu, setActiveConversationMenu] = useState<string | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isTendooAiVisible, setIsTendooAiVisible] = useState(true);
  const [tendooAiPosition, setTendooAiPosition] = useState<AiDisplayPosition>('all');
  const [aiDisplaySettingId, setAiDisplaySettingId] = useState<string | null>(null);
  const [aiDisplayReferenceId, setAiDisplayReferenceId] = useState<string | null>(null);
  const [isAiDisplayLoading, setIsAiDisplayLoading] = useState(false);
  const [conversations, setConversations] = useState<ConversationItem[]>([]);
  /** Có lịch sử thật (không phụ thuộc kết quả search) — dùng để ẩn/hiện cả khối lịch sử */
  const [hasChatHistory, setHasChatHistory] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMoreSessions, setHasMoreSessions] = useState(false);
  const [isSessionsLoading, setIsSessionsLoading] = useState(false);
  const [isSessionsLoadingMore, setIsSessionsLoadingMore] = useState(false);
  const sessionListRef = useRef<HTMLDivElement>(null);
  const sessionsRequestIdRef = useRef(0);

  // Áp dụng personalization từ bootstrap khi mở chatbot
  useEffect(() => {
    if (!aiDisplayFromOpen || aiDisplayOpenVersion === 0) return;

    setAiDisplaySettingId(aiDisplayFromOpen.id);
    setAiDisplayReferenceId(aiDisplayFromOpen.referenceId);
    setIsTendooAiVisible(aiDisplayFromOpen.isVisible);
    setTendooAiPosition(aiDisplayFromOpen.position);
  }, [aiDisplayFromOpen, aiDisplayOpenVersion]);

  // Mở side sheet → gọi lại nếu chưa có setting (fallback)
  useEffect(() => {
    if (!open) return;

    const token = sessionStorage.getItem('access_token');
    if (!token) return;

    let cancelled = false;

    const loadAiDisplaySetting = async () => {
      setIsAiDisplayLoading(true);
      try {
        const setting = await chatService.getOrCreateAiDisplaySetting(token);
        if (cancelled || !setting) return;

        setAiDisplaySettingId(setting.id);
        setAiDisplayReferenceId(setting.referenceId);
        setIsTendooAiVisible(setting.isVisible);
        setTendooAiPosition(setting.position);
      } catch (error) {
        if (!cancelled) {
          console.error('[ChatSideSheet] Failed to load AI display setting:', error);
        }
      } finally {
        if (!cancelled) {
          setIsAiDisplayLoading(false);
        }
      }
    };

    void loadAiDisplaySetting();

    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchKeyword(searchInput.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const loadSessions = useCallback(
    async (options?: { cursor?: string | null; append?: boolean }) => {
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        setConversations([]);
        setNextCursor(null);
        setHasMoreSessions(false);
        if (!searchKeyword) setHasChatHistory(false);
        return;
      }

      const append = Boolean(options?.append);
      const requestId = ++sessionsRequestIdRef.current;
      const isUnfiltered = !searchKeyword;

      if (append) {
        setIsSessionsLoadingMore(true);
      } else {
        setIsSessionsLoading(true);
        // Clear list on fresh fetch (incl. search) so UI doesn't look like local-only filter
        setConversations([]);
        setNextCursor(null);
        setHasMoreSessions(false);
      }

      try {
        console.log('[ChatSideSheet] GET sessions', {
          typeChat: ChatAgentType.AI,
          keyword: searchKeyword || undefined,
          append,
        });
        // Luôn list lịch sử AI Hỏi đáp (không theo agent đang chọn)
        const page = await chatService.getMessageSessions(token, {
          size: 20,
          typeChat: ChatAgentType.AI,
          keyword: searchKeyword || undefined,
          nextCursor: options?.cursor || undefined,
          direction: 'NEXT',
        });

        if (requestId !== sessionsRequestIdRef.current) return;

        const mapped = page.data.map(mapSessionToConversation);
        setConversations((prev) => (append ? [...prev, ...mapped] : mapped));
        setNextCursor(page.nextCursor);
        setHasMoreSessions(page.hasMore);

        // Chỉ cập nhật hasChatHistory khi load không search — search rỗng ≠ chưa có lịch sử
        if (isUnfiltered) {
          if (!append) {
            setHasChatHistory(mapped.length > 0);
          } else if (mapped.length > 0) {
            setHasChatHistory(true);
          }
        }
      } catch (error) {
        if (requestId !== sessionsRequestIdRef.current) return;
        console.error('[ChatSideSheet] Failed to load message sessions:', error);
        if (!append) {
          setConversations([]);
          setNextCursor(null);
          setHasMoreSessions(false);
          if (isUnfiltered) setHasChatHistory(false);
        }
      } finally {
        if (requestId === sessionsRequestIdRef.current) {
          setIsSessionsLoading(false);
          setIsSessionsLoadingMore(false);
        }
      }
    },
    [searchKeyword],
  );

  useEffect(() => {
    if (!open) return;
    void loadSessions({ append: false });
  }, [open, loadSessions, sessionsRefreshKey]);

  const handleSessionsScroll = () => {
    const el = sessionListRef.current;
    if (!el || isSessionsLoading || isSessionsLoadingMore || !hasMoreSessions || !nextCursor) {
      return;
    }

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom <= 40) {
      void loadSessions({ cursor: nextCursor, append: true });
    }
  };

  /** Notify seller host. Position changes omit isShowChatbot — parent recomputes by route. */
  const notifyTendooAiDisplay = (
    nextVisible: boolean,
    nextPosition: AiDisplayPosition,
    options?: { forPositionChange?: boolean },
  ) => {
    const forPositionChange = Boolean(options?.forPositionChange);

    safePostMessage({
      type: PostMessageType.CONFIG_TENDO_AI,
      data: {
        ...(forPositionChange
          ? { enabled: nextVisible, position: nextPosition }
          : { isShowChatbot: nextVisible, position: nextPosition }),
        configReady: true,
        settingId: aiDisplaySettingId,
        referenceId: aiDisplayReferenceId,
      },
    });
  };

  const handleToggleTendooAi = (checked: boolean) => {
    setIsTendooAiVisible(checked);
    notifyTendooAiDisplay(checked, tendooAiPosition);
  };

  const handleChangeTendooAiPosition = async (position: AiDisplayPosition) => {
    const token = sessionStorage.getItem('access_token');
    if (!token) {
      console.warn('[ChatSideSheet] AI display position update skipped — no access_token');
      return;
    }

    if (!aiDisplaySettingId) {
      console.warn('[ChatSideSheet] AI display position update skipped — no setting id');
      return;
    }

    try {
      const nextSetting = await chatService.updateAiDisplaySetting(
        token,
        aiDisplaySettingId,
        true,
        position,
        aiDisplayReferenceId,
      );

      const nextPosition = nextSetting?.position ?? position;
      const nextVisible = nextSetting?.isVisible ?? true;

      setTendooAiPosition(nextPosition);
      setIsTendooAiVisible(nextVisible);
      if (nextSetting?.referenceId) {
        setAiDisplayReferenceId(nextSetting.referenceId);
      }
      notifyTendooAiDisplay(nextVisible, nextPosition, { forPositionChange: true });
    } catch (error) {
      console.error('[ChatSideSheet] Failed to update AI display setting:', error);
    }
  };

  const handleToggleAudio = () => {
    onToggleAudio?.();
  };

  const applyRenameConversation = useCallback(async (conversationId: string, newTitle: string) => {
    const nextTitle = newTitle.trim().slice(0, 32);
    if (!nextTitle) return;

    const token = sessionStorage.getItem('access_token');
    if (!token) {
      console.warn('[ChatSideSheet] rename skipped — no access_token');
      return;
    }

    try {
      const savedName = await chatService.renameMessageSession(token, conversationId, nextTitle);
      setConversations((prev) =>
        prev.map((item) =>
          item.id === conversationId ? { ...item, title: savedName } : item,
        ),
      );
      safePostMessage({
        type: PostMessageType.CHAT_CONVERSATION_RENAME_SUCCESS,
        payload: { conversationId },
      });
    } catch (error) {
      console.error('[ChatSideSheet] Failed to rename message session:', error);
    }
  }, []);

  const applyDeleteConversation = useCallback(async (conversationId: string) => {
    const token = sessionStorage.getItem('access_token');
    if (!token) {
      console.warn('[ChatSideSheet] delete skipped — no access_token');
      return;
    }

    try {
      await chatService.deleteMessageSession(token, conversationId);
      setConversations((prev) => {
        const next = prev.filter((item) => item.id !== conversationId);
        if (!searchKeyword && next.length === 0) {
          setHasChatHistory(false);
        }
        return next;
      });
      safePostMessage({
        type: PostMessageType.CHAT_CONVERSATION_DELETE_SUCCESS,
        payload: { conversationId },
      });

      const activeId = sessionStorage.getItem(MESSAGE_SESSION_STORAGE_KEY);
      if (activeId === conversationId) {
        // Soft-deleted active session → pick/create a new current session
        try {
          const current = await chatService.getCurrentMessageSession(token, typeChat);
          setMessageSessionId(current?.sessionId ?? null);
        } catch (error) {
          console.error('[ChatSideSheet] Failed to refresh current session after delete:', error);
          setMessageSessionId(null);
        }
      }
    } catch (error) {
      console.error('[ChatSideSheet] Failed to delete message session:', error);
    }
  }, [typeChat, searchKeyword]);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (!isAllowedParentOrigin(event.origin)) {
        return;
      }

      const { type, payload, data } = event.data || {};

      if (type === PostMessageType.CONFIG_TENDO_AI) {
        const result = data || payload || {};
        if (result.isShowChatbot === true) {
          setIsTendooAiVisible(true);
        }
        return;
      }

      if (!payload?.conversationId) return;

      if (type === PostMessageType.CHAT_CONVERSATION_RENAME_RESULT) {
        if (payload.confirmed && typeof payload.newTitle === 'string') {
          void applyRenameConversation(payload.conversationId, payload.newTitle);
        }
        return;
      }

      if (type === PostMessageType.CHAT_CONVERSATION_DELETE_RESULT) {
        if (payload.confirmed) {
          void applyDeleteConversation(payload.conversationId);
        }
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [applyRenameConversation, applyDeleteConversation]);

  const openRenameDialog = (conversation: { id: string; title: string }) => {
    setActiveConversationMenu(null);
    safePostMessage({
      type: PostMessageType.CHAT_CONVERSATION_RENAME_REQUEST,
      payload: {
        conversationId: conversation.id,
        title: conversation.title,
      },
    });
  };

  const openDeleteDialog = (conversation: { id: string; title: string }) => {
    setActiveConversationMenu(null);
    safePostMessage({
      type: PostMessageType.CHAT_CONVERSATION_DELETE_REQUEST,
      payload: {
        conversationId: conversation.id,
        title: conversation.title,
      },
    });
  };

  const isAiDisplayDisabled = isAiDisplayLoading;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="left"
      width={310}
      closeIcon={null}
      title={null}
      rootClassName="chat-side-sheet-drawer"
      getContainer={getContainer}
      maskClosable
    >
      <aside
        className="flex h-full w-full flex-col gap-3 rounded-r-[24px] bg-[rgba(248,248,249,1)] px-[14px] pt-6 pb-5 shadow-[12px_0_32px_rgba(17,17,20,0.12)] box-border"
        aria-label="Chat side menu"
      >
        <div className="flex items-center justify-between gap-3 px-[2px]">
          <ViettelTendooAIDrawer />
          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border-0 bg-white shadow-[0_2px_8px_rgba(17,17,20,0.06)] outline-none hover:bg-[#f7f7f8]"
            onClick={onClose}
            aria-label="Đóng menu"
          >
            <CloseIcon />
          </button>
        </div>

        {typeChat === ChatAgentType.AI && (
          <button
            type="button"
            className="flex h-10 w-full shrink-0 items-center gap-[10px] rounded-full border-0 bg-[rgba(255,255,255,0.92)] px-[14px] text-left text-[#242428] shadow-[inset_0_0_0_1px_#ececf0] outline-none"
            onClick={() => {
              void (async () => {
                await onNewChat?.();
                onClose();
              })();
            }}
          >
            <NewChatIcon />
            <span className="text-sm font-medium leading-[18px]">Cuộc trò chuyện mới</span>
          </button>
        )}

        <div
          className={`w-full shrink-0 overflow-hidden border border-[#ececf0] bg-[rgba(255,255,255,0.92)] ${
            isSettingsOpen ? 'rounded-[20px]' : 'rounded-full'
          }`}
        >
          <button
            type="button"
            className="flex h-10 w-full items-center justify-between border-0 bg-transparent px-[14px] text-left text-[#242428] outline-none"
            onClick={() => setIsSettingsOpen((prev) => !prev)}
            aria-expanded={isSettingsOpen}
          >
            <div className="flex items-center gap-[10px]">
              <SettingIcon />
              <span className="text-sm font-medium leading-[18px]">Cài đặt</span>
            </div>
            <DownOutlined
              className={`text-xs text-[#5c5c66] transition-transform duration-200 ${
                isSettingsOpen ? 'rotate-180' : ''
              }`}
            />
          </button>

          {isSettingsOpen && (
            <div className="flex flex-col gap-2 px-2 pb-2">
              <div className={`w-full overflow-hidden border border-[#ececf0] bg-white ${
                isTendooAiVisible ? 'rounded-[16px]' : 'rounded-full'
              }`}>
                <div className="chat-side-sheet-setting-row border-0 bg-transparent shadow-none !rounded-none !p-2">
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <span className="chat-side-sheet-setting-icon">
                      <CommonQAIcon />
                    </span>
                    <span className="text-sm font-medium leading-[18px] text-[#242428]">
                      Hiển thị Tendoo AI
                    </span>
                  </div>
                  <Switch
                    size="small"
                    checked={isTendooAiVisible}
                    disabled={isAiDisplayDisabled}
                    onChange={handleToggleTendooAi}
                    onClick={(_, event) => event.stopPropagation()}
                  />
                </div>

                {isTendooAiVisible && (
                  <div className="flex items-center justify-between gap-3 border-t border-[rgba(242,242,244,1)] p-2">
                    <div className="text-sm font-medium leading-[18px] text-[#3D3D43]">
                      Vị trí hiện
                    </div>
                    <div className="flex h-7 w-[153px] flex-none gap-3 rounded-[8px] bg-[#f7f7f8] p-[2px]">
                      <button
                        type="button"
                        disabled={isAiDisplayDisabled}
                        className={`flex h-6 flex-1 items-center justify-center rounded-[8px] border-0 bg-transparent px-0 text-sm leading-[18px] transition-colors disabled:opacity-50 ${
                          tendooAiPosition === 'all'
                            ? 'bg-white font-medium text-[#242428]'
                            : 'font-normal text-[#494951]'
                        }`}
                        onClick={() => handleChangeTendooAiPosition('all')}
                      >
                        Tất cả
                      </button>
                      <button
                        type="button"
                        disabled={isAiDisplayDisabled}
                        className={`flex h-6 flex-1 items-center justify-center rounded-[8px] border-0 bg-transparent px-0 text-sm leading-[18px] transition-colors disabled:opacity-50 ${
                          tendooAiPosition === 'home'
                            ? 'bg-white font-medium text-[#242428]'
                            : 'font-normal text-[#494951]'
                        }`}
                        onClick={() => handleChangeTendooAiPosition('home')}
                      >
                        Trang chủ
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="chat-side-sheet-setting-row">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <span
                    className={`chat-side-sheet-setting-icon ${
                      isBotSpeaking && isAudioEnabled ? 'is-speaking' : ''
                    }`}
                  >
                    {isAudioEnabled ? <VolumeIcon /> : <VolumeMuteIcon />}
                  </span>
                  <span className="text-sm font-medium leading-[18px] text-[#242428]">Âm thanh</span>
                </div>
                <Switch
                  size="small"
                  checked={isAudioEnabled}
                  onChange={handleToggleAudio}
                  onClick={(_, event) => event.stopPropagation()}
                />
              </div>
            </div>
          )}
        </div>

        {/* Ẩn cả khối chỉ khi chưa từng có lịch sử; search rỗng vẫn giữ title + ô search */}
        {hasChatHistory && (
          <>
            <div className="mt-[2px] shrink-0 px-1 text-[14px] font-medium leading-[18px] tracking-normal text-[#94949e]">
              LỊCH SỬ TRÒ CHUYỆN VỚI AI HỎI ĐÁP
            </div>

            <div className="flex h-10 shrink-0 items-center gap-2 rounded-full bg-[rgba(255,255,255,0.92)] px-[14px] shadow-[inset_0_0_0_1px_#ececf0]">
              <SearchChatHistoryIcon />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    setSearchKeyword(searchInput.trim());
                  }
                }}
                className="flex-1 border-0 bg-transparent text-sm leading-[18px] text-[#242428] outline-none placeholder:text-[#94949e]"
                placeholder="Tìm kiếm cuộc trò chuyện"
              />
            </div>

            <div
              ref={sessionListRef}
              onScroll={handleSessionsScroll}
              className="flex min-h-0 flex-1 flex-col overflow-y-auto rounded-2xl bg-[rgba(255,255,255,0.82)] py-2 shadow-[inset_0_0_0_1px_#ececf0]"
            >
              {isSessionsLoading && conversations.length === 0 && (
                <div className="flex items-center justify-center gap-2 px-4 py-6 text-sm text-[#94949e]">
                  <LoadingOutlined />
                  <span>Đang tải...</span>
                </div>
              )}

              {!isSessionsLoading && conversations.length === 0 && searchKeyword.trim() && (
                <div className="flex flex-1 flex-col items-center justify-center gap-2 px-4 py-6">
                  <SearchNoResultIcon className="h-[140px] w-[140px] shrink-0" />
                  <p className="m-0 text-center text-base font-medium leading-[22px] text-[#242428]">
                    Không có kết quả tìm kiếm
                  </p>
                  <p className="m-0 text-center text-xs font-normal leading-4 text-[#62626A]">
                    Thử từ khoá khác hoặc kiểm tra chính tả nhé
                  </p>
                </div>
              )}

              {conversations.map((conversation) => {
                const isMenuOpen = activeConversationMenu === conversation.id;

                return (
                  <div
                    key={conversation.id}
                    className="flex min-h-11 w-full items-center justify-between gap-3 px-[14px] text-left text-[#242428] hover:bg-[rgba(61,61,67,0.04)]"
                  >
                    <button
                      type="button"
                      className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap border-0 bg-transparent p-0 text-left text-sm leading-[18px] text-[#242428] outline-none"
                      onClick={() => {
                        setMessageSessionId(conversation.id);
                        onSelectConversation?.(conversation);
                        onClose();
                      }}
                    >
                      {conversation.title}
                    </button>
                    <Dropdown
                      trigger={['click']}
                      placement="bottomRight"
                      open={isMenuOpen}
                      onOpenChange={(nextOpen) =>
                        setActiveConversationMenu(nextOpen ? conversation.id : null)
                      }
                      items={[
                        {
                          key: 'rename',
                          prevIcon: <EditChatNameIcon />,
                          label: 'Đổi tên',
                          onClick: () => openRenameDialog(conversation),
                        },
                        {
                          key: 'delete',
                          prevIcon: <DeleteChatIcon />,
                          label: <span className="text-[#FF2E4C]">Xóa</span>,
                          onClick: () => openDeleteDialog(conversation),
                        },
                      ]}
                      width={156}
                    >
                      <button
                        type="button"
                        className={`inline-flex h-8 w-8 items-center justify-center rounded-full border-0 text-[#5c5c66] outline-none ${isMenuOpen
                          ? 'bg-white shadow-[0_2px_8px_rgba(17,17,20,0.06)]'
                          : 'bg-transparent shadow-none'
                          }`}
                        aria-label={`Tùy chọn cho ${conversation.title}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreOutlined className="rotate-90 text-base" />
                      </button>
                    </Dropdown>
                  </div>
                );
              })}

              {isSessionsLoadingMore && (
                <div className="flex items-center justify-center gap-2 px-4 py-3 text-xs text-[#94949e]">
                  <LoadingOutlined />
                  <span>Đang tải thêm...</span>
                </div>
              )}
            </div>
          </>
        )}
      </aside>
    </Drawer>
  );
};

export default ChatSideSheet;
